// Analytics → Online quotes: what the website Quote Tool brings in and what it
// turns into. Requests → quotes → accepted → invoiced, by source (click IDs /
// UTM / referrer), campaign, service and week. Data comes from
// /analytics/online-quotes (see backend onlineQuoteAnalytics.ts).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type OnlineQuoteAnalytics, type OnlineQuoteFunnel } from '../api';

function todayBne(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function firstOfMonth(ymd: string): string { const [y, m] = ymd.split('-'); return `${y}-${m}-01`; }
function addMonths(ymd: string, n: number): string { const [y, m] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10); }
function fmtDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const money = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n || 0);
const pct = (num: number, den: number) => (den ? `${Math.round((num / den) * 100)}%` : '–');

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', background: 'var(--color-surface)', padding: 'var(--spacing-4)' };
const btn: React.CSSProperties = { padding: 'var(--spacing-2) var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' };
const btnActive: React.CSSProperties = { ...btn, background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)', fontWeight: 600 };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '6px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--font-size-sm)', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

type Preset = '30d' | '90d' | 'month' | 'lastMonth' | 'year' | 'custom';

function rangeFor(p: Preset, today: string): { from: string; to: string } {
  const tomorrow = addDays(today, 1);
  switch (p) {
    case '30d': return { from: addDays(today, -29), to: tomorrow };
    case '90d': return { from: addDays(today, -89), to: tomorrow };
    case 'month': return { from: firstOfMonth(today), to: tomorrow };
    case 'lastMonth': return { from: addMonths(firstOfMonth(today), -1), to: firstOfMonth(today) };
    case 'year': return { from: `${today.slice(0, 4)}-01-01`, to: tomorrow };
    default: return { from: addDays(today, -29), to: tomorrow };
  }
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ ...card, padding: 'var(--spacing-3)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** Horizontal funnel: each stage as a bar relative to requests. */
function Funnel({ t }: { t: OnlineQuoteFunnel }) {
  const stages: Array<[string, number, string]> = [
    ['Requests', t.requests, 'var(--color-light-blue, #8DD2E5)'],
    ['Quoted', t.quoted, 'var(--color-accent)'],
    ['Sent', t.sent, 'var(--color-accent)'],
    ['Accepted', t.accepted, 'var(--color-success, #2e7d32)'],
    ['Invoiced', t.invoiced, 'var(--color-success, #2e7d32)'],
  ];
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {stages.map(([label, n, color]) => (
        <div key={label} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 60px 50px', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-sm)' }}>
          <span>{label}</span>
          <div style={{ height: 14, background: 'var(--color-background)', borderRadius: 7, overflow: 'hidden' }}>
            <div style={{ width: t.requests ? `${Math.max(2, (n / t.requests) * 100)}%` : '0%', height: '100%', background: color, borderRadius: 7 }} />
          </div>
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{n}</span>
          <span style={{ textAlign: 'right', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{pct(n, t.requests)}</span>
        </div>
      ))}
    </div>
  );
}

/** Weekly bars: requests (light) with quoted and accepted overlaid. */
function WeeklyBars({ weeks }: { weeks: OnlineQuoteAnalytics['byWeek'] }) {
  const max = Math.max(1, ...weeks.map((w) => w.requests));
  if (!weeks.length) return <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>No requests in this period.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 150, minWidth: weeks.length * 44 }}>
        {weeks.map((w) => (
          <div key={w.weekStart} title={`Week of ${fmtDay(w.weekStart)}: ${w.requests} requests, ${w.quoted} quoted, ${w.accepted} accepted`} style={{ flex: '1 0 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%' }}>
            <div style={{ position: 'relative', width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${(w.requests / max) * 100}%`, background: 'var(--color-light-blue, #8DD2E5)', borderRadius: '4px 4px 0 0' }} />
              <div style={{ position: 'absolute', bottom: 0, left: '15%', right: '15%', height: `${(w.quoted / max) * 100}%`, background: 'var(--color-accent)', borderRadius: '4px 4px 0 0' }} />
              <div style={{ position: 'absolute', bottom: 0, left: '30%', right: '30%', height: `${(w.accepted / max) * 100}%`, background: 'var(--color-success, #2e7d32)', borderRadius: '4px 4px 0 0' }} />
            </div>
            <span style={{ fontSize: 10, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{fmtDay(w.weekStart)}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 6 }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-light-blue, #8DD2E5)', borderRadius: 2, marginRight: 4 }} />Requests</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-accent)', borderRadius: 2, marginRight: 4 }} />Quoted</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-success, #2e7d32)', borderRadius: 2, marginRight: 4 }} />Accepted</span>
      </div>
    </div>
  );
}

function GroupTable({ title, rows, keyLabel, hint }: { title: string; rows: Array<OnlineQuoteFunnel & { key: string }>; keyLabel: string; hint?: string }) {
  return (
    <div style={card}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>{title}</h3>
      {hint && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{hint}</div>}
      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 0 }}>Nothing in this period.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 'var(--spacing-2)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>{keyLabel}</th>
                <th style={{ ...th, textAlign: 'right' }}>Requests</th>
                <th style={{ ...th, textAlign: 'right' }}>Quoted</th>
                <th style={{ ...th, textAlign: 'right' }}>Accepted</th>
                <th style={{ ...th, textAlign: 'right' }}>Win rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Quoted $</th>
                <th style={{ ...th, textAlign: 'right' }}>Accepted $</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td style={td}>{r.key}</td>
                  <td style={num}>{r.requests}</td>
                  <td style={num}>{r.quoted}</td>
                  <td style={num}>{r.accepted}</td>
                  <td style={num} title="Accepted or invoiced ÷ quoted">{pct(r.accepted, r.quoted)}</td>
                  <td style={num}>{money(r.quotedValue)}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{money(r.acceptedValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const OUTCOME_LABEL: Record<OnlineQuoteAnalytics['recent'][number]['outcome'], [string, string]> = {
  pending: ['Awaiting quote', 'var(--color-warning, #b26a00)'],
  dismissed: ['Dismissed', 'var(--color-text-muted)'],
  quoted: ['Draft quote', 'var(--color-text-muted)'],
  sent: ['Quote sent', 'var(--color-accent)'],
  accepted: ['Accepted', 'var(--color-success, #2e7d32)'],
  invoiced: ['Invoiced', 'var(--color-success, #2e7d32)'],
  declined: ['Declined', 'var(--color-error)'],
};

export function Analytics() {
  const navigate = useNavigate();
  const today = useMemo(() => todayBne(), []);
  const [preset, setPreset] = useState<Preset>('30d');
  const [custom, setCustom] = useState(() => rangeFor('30d', today));
  const range = preset === 'custom' ? custom : rangeFor(preset, today);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['online-quote-analytics', range.from, range.to],
    queryFn: () => api.getOnlineQuoteAnalytics(range.from, range.to),
  });
  const t = data?.totals;

  const presets: Array<[Preset, string]> = [['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['month', 'This month'], ['lastMonth', 'Last month'], ['year', 'This year'], ['custom', 'Custom']];

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Online quotes</h1>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Website quote requests and what they turned into · {fmtDay(range.from)} to {fmtDay(addDays(range.to, -1))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {presets.map(([p, label]) => (
            <button key={p} style={preset === p ? btnActive : btn} onClick={() => setPreset(p)}>{label}</button>
          ))}
          {preset === 'custom' && (
            <>
              <input type="date" value={custom.from} max={custom.to} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} style={{ ...btn, cursor: 'auto' }} />
              <span style={{ color: 'var(--color-text-muted)' }}>to</span>
              <input type="date" value={addDays(custom.to, -1)} min={custom.from} onChange={(e) => setCustom((c) => ({ ...c, to: addDays(e.target.value, 1) }))} style={{ ...btn, cursor: 'auto' }} />
            </>
          )}
        </div>
      </div>

      {isError && <div style={{ ...card, color: 'var(--color-error)' }}>{(error as Error)?.message || 'Could not load analytics.'}</div>}
      {isLoading && <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div>}

      {data && t && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--spacing-3)' }}>
            <Tile label="Requests" value={String(t.requests)} sub={`${t.pending} awaiting a quote · ${t.dismissed} dismissed`} />
            <Tile label="Quoted" value={String(t.quoted)} sub={`${pct(t.quoted, t.requests)} of requests`} />
            <Tile label="Accepted" value={String(t.accepted)} sub={`${pct(t.accepted, t.quoted)} of quotes · ${t.invoiced} invoiced`} />
            <Tile label="Quoted value" value={money(t.quotedValue)} sub="inc GST" />
            <Tile label="Accepted value" value={money(t.acceptedValue)} sub="accepted + invoiced, inc GST" />
            <Tile label="Time to quote" value={t.medianHoursToQuote == null ? '–' : t.medianHoursToQuote < 48 ? `${t.medianHoursToQuote} h` : `${Math.round(t.medianHoursToQuote / 24)} d`} sub="median, request → quote created" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--spacing-3)' }}>
            <div style={card}>
              <h3 style={{ margin: '0 0 var(--spacing-3)', fontSize: 'var(--font-size-md)' }}>Funnel</h3>
              <Funnel t={t} />
            </div>
            <div style={card}>
              <h3 style={{ margin: '0 0 var(--spacing-3)', fontSize: 'var(--font-size-md)' }}>Requests per week</h3>
              <WeeklyBars weeks={data.byWeek} />
            </div>
          </div>

          <GroupTable title="By source" keyLabel="Source" rows={data.bySource} hint="Measured from the click ID, UTM tags or referrer captured on the website. “Unknown” = requests received before capture was live, or with tracking blocked." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--spacing-3)' }}>
            <GroupTable title="By campaign" keyLabel="Campaign" rows={data.byCampaign} hint="utm_campaign, or the Google Ads campaign id when no UTM was set." />
            <GroupTable title="By property type" keyLabel="Type" rows={data.byPropertyType} />
          </div>
          <GroupTable title="By service requested" keyLabel="Service" rows={data.byService} hint="A request asking for two services counts under both." />

          <div style={card}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Recent requests</h3>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>Latest 50 in the period. Click a quote number to open it.</div>
            <div style={{ overflowX: 'auto', marginTop: 'var(--spacing-2)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Received</th>
                    <th style={th}>Name</th>
                    <th style={th}>Source</th>
                    <th style={th}>Services</th>
                    <th style={th}>Outcome</th>
                    <th style={th}>Quote</th>
                    <th style={{ ...th, textAlign: 'right' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.length === 0 && (
                    <tr><td style={td} colSpan={7}><span style={{ color: 'var(--color-text-muted)' }}>No requests in this period.</span></td></tr>
                  )}
                  {data.recent.map((r) => {
                    const [label, color] = OUTCOME_LABEL[r.outcome];
                    return (
                      <tr key={r.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.receivedAt)}</td>
                        <td style={td}>{r.name}</td>
                        <td style={td}>{r.source}</td>
                        <td style={td}>{r.services.length ? r.services.map((s) => s.replace(/\s*\(.*\)$/, '')).join(', ') : '–'}</td>
                        <td style={{ ...td, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</td>
                        <td style={td}>
                          {r.quoteId ? (
                            <button style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer', font: 'inherit' }} onClick={() => navigate(`/billing?quote=${encodeURIComponent(r.quoteId!)}`)}>
                              {r.quoteNumber ?? 'Open quote'}
                            </button>
                          ) : '–'}
                        </td>
                        <td style={num}>{r.quoteTotal != null ? money(r.quoteTotal) : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
